import React, { useEffect, useMemo, useState } from "react";
import { logError } from "@/lib/logger";
import api from "@/lib/api";
import {
    ChartBar,
    Briefcase,
    UsersThree,
    Trophy,
    Hourglass,
    TrendUp,
    Globe,
    User,
    Upload,
} from "@phosphor-icons/react";
import ShareAnalyticsPanel from "./analytics/ShareAnalyticsPanel";

const STAGES = ["Applied", "Screening", "Interview", "Offer", "Hired", "Rejected"];

function Kpi({ label, value, suffix, icon: Icon, accent }) {
    return (
        <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6 flex flex-col gap-2 sm:gap-3">
            <div className="flex items-center justify-between">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
                    {label}
                </p>
                <Icon size={18} weight="bold" className={accent || "text-[#A3A3A3]"} />
            </div>
            <p className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-ink">
                {value}
                {suffix && (
                    <span className="font-sans text-sm sm:text-lg font-bold text-[#525252] ml-1">
                        {suffix}
                    </span>
                )}
            </p>
        </div>
    );
}

function SourceIcon({ source }) {
    if (source === "public") return <Globe size={14} weight="bold" />;
    if (source === "csv") return <Upload size={14} weight="bold" />;
    return <User size={14} weight="bold" />;
}

export default function Analytics() {
    const [data, setData] = useState(null);

    useEffect(() => {
        api
            .get("/analytics")
            .then((r) => setData(r.data))
            .catch((e) => logError("Failed to load analytics:", e));
    }, []);

    const sortedSources = useMemo(
        () => Object.entries(data?.sources || {}).sort((a, b) => b[1] - a[1]),
        [data?.sources],
    );

    if (!data)
        return (
            <div className="px-4 sm:px-8 py-10 text-[#525252]" data-testid="analytics-loading">
                Loading…
            </div>
        );

    const maxStage = Math.max(...STAGES.map((s) => data.pipeline?.[s] || 0), 1);
    const sourceTotal = Object.values(data.sources || {}).reduce((a, b) => a + b, 0) || 1;

    return (
        <div className="px-3 sm:px-8 py-5 sm:py-10 max-w-[1600px] mx-auto overflow-x-hidden" data-testid="analytics-page">
            <div className="mb-8 sm:mb-10">
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                    Insights · Last 90 days
                </p>
                <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink">
                    Analytics.
                </h1>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8 sm:mb-10">
                <Kpi
                    label="Avg. time-to-hire"
                    value={data.avg_time_to_hire_days}
                    suffix="days"
                    icon={Hourglass}
                />
                <Kpi
                    label="Conversion rate"
                    value={data.conversion_rate}
                    suffix="%"
                    icon={TrendUp}
                />
                <Kpi label="Open requirements" value={data.open_jobs} icon={Briefcase} />
                <Kpi label="Hires made" value={data.hired} icon={Trophy} accent="text-[#059669]" />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mb-8">
                {/* Funnel */}
                <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
                            Pipeline funnel
                        </p>
                        <ChartBar size={16} weight="bold" className="text-[#A3A3A3]" />
                    </div>
                    <div className="space-y-3">
                        {STAGES.map((s) => {
                            const n = data.pipeline?.[s] || 0;
                            const pct = (n / maxStage) * 100;
                            return (
                                <div key={s} data-testid={`funnel-${s.toLowerCase()}`}>
                                    <div className="flex items-baseline justify-between mb-1">
                                        <span className="text-xs font-black uppercase tracking-wider text-ink">
                                            {s}
                                        </span>
                                        <span className="text-xs font-bold text-[#525252]">
                                            {n}
                                        </span>
                                    </div>
                                    <div className="h-2 bg-[#F5F5F5] relative">
                                        <div
                                            className={`absolute inset-y-0 left-0 transition-all duration-700 ${
                                                s === "Hired"
                                                    ? "bg-[#059669]"
                                                    : s === "Rejected"
                                                      ? "bg-[#DC2626]"
                                                      : "bg-cobalt"
                                            }`}
                                            style={{ width: `${pct}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Sources */}
                <div className="bg-white border border-[#E5E5E5] p-4 sm:p-6">
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
                            Candidate sources
                        </p>
                        <UsersThree size={16} weight="bold" className="text-[#A3A3A3]" />
                    </div>
                    <div className="space-y-4">
                        {sortedSources.map(([src, n]) => {
                                const pct = Math.round((n / sourceTotal) * 100);
                                return (
                                    <div key={src} data-testid={`source-${src}`}>
                                        <div className="flex items-center justify-between mb-1">
                                            <span className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-ink">
                                                <SourceIcon source={src} />
                                                {src}
                                            </span>
                                            <span className="text-xs font-bold text-[#525252]">
                                                {n} · {pct}%
                                            </span>
                                        </div>
                                        <div className="h-2 bg-[#F5F5F5] relative">
                                            <div
                                                className="absolute inset-y-0 left-0 bg-ink"
                                                style={{ width: `${pct}%` }}
                                            />
                                        </div>
                                    </div>
                                );
                            })}
                        {(!data.sources || !Object.keys(data.sources).length) && (
                            <p className="text-sm text-[#A3A3A3]">No candidates yet.</p>
                        )}
                    </div>
                </div>
            </div>

            {/* Top jobs */}
            <div className="bg-white border border-[#E5E5E5]">
                <div className="p-4 sm:p-6 border-b border-[#E5E5E5]">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3]">
                        Top requirements by volume
                    </p>
                </div>
                {(data.top_jobs || []).length === 0 ? (
                    <p className="p-8 text-center text-[#525252]">No data yet.</p>
                ) : (
                    data.top_jobs.map((j, i) => (
                        <div
                            key={j.job_id}
                            data-testid={`top-job-${i}`}
                            className="flex items-center justify-between p-4 sm:p-5 border-b border-[#E5E5E5] last:border-b-0"
                        >
                            <div className="flex items-center gap-4 min-w-0">
                                <span className="font-heading font-black text-2xl tracking-tighter text-[#A3A3A3] w-8">
                                    {(i + 1).toString().padStart(2, "0")}
                                </span>
                                <div className="min-w-0">
                                    <p className="font-bold text-ink truncate">{j.title}</p>
                                    <p className="text-xs text-[#525252]">
                                        {j.department || "General"}
                                    </p>
                                </div>
                            </div>
                            <span className="text-sm font-bold text-ink shrink-0">
                                {j.count} candidates
                            </span>
                        </div>
                    ))
                )}
            </div>

            <ShareAnalyticsPanel />
        </div>
    );
}
